import {
  createGroup,
  updateGroup,
  createField,
  updateField,
  deleteField,
  createSelectOption,
  updateSelectOption,
  deleteSelectOption,
} from "@/api/requests";

export function useGroupSync({ id, fetchData, setPending }) {
  const saveGroup = async (group, values) => {
    try {
      setPending(true);

      const groupPayload = {
        name: values.name,
        request_form_uuid: id,
        idx: group.idx,
      };

      const savedGroup = group.isNew
        ? await createGroup(groupPayload)
        : await updateGroup({ uuid: group.uuid, ...groupPayload });

      const groupUuid = savedGroup.uuid;

      await syncFields({
        group,
        values,
        groupUuid,
        requestId: id,
      });

      await fetchData();
    } finally {
      setPending(false);
    }
  };

  return { saveGroup };
}

async function syncFields({ group, values, groupUuid, requestId }) {
  const prevFields = group.fields || [];
  const nextFields = values.fields || [];

  const nextFieldUuids = new Set(
    nextFields.filter((f) => f.uuid).map((f) => f.uuid),
  );
  for (const f of prevFields) {
    if (f.uuid && !nextFieldUuids.has(f.uuid)) {
      await deleteField({ uuid: f.uuid });
    }
  }

  const normalizedFields = nextFields.map((f, index) => ({
    ...f,
    idx: index + 1,
  }));

  for (let i = 0; i < normalizedFields.length; i++) {
    const field = normalizedFields[i];

    const payload = {
      name: field.name,
      type: field.type,
      idx: i + 1,
      is_required: field.is_required,
      request_form_uuid: requestId,
      request_form_group_uuid: groupUuid,
      placeholder: field.placeholder,
    };

    const savedField = !field.uuid
      ? await createField(payload)
      : await updateField({ uuid: field.uuid, ...payload });

    if (field.type === "select") {
      await syncOptions({
        prevField: prevFields.find((f) => f.uuid === field.uuid),
        field,
        fieldUuid: savedField.uuid,
      });
    }
  }
}

async function syncOptions({ prevField, field, fieldUuid }) {
  const prevOptions = prevField?.options || [];
  const nextOptions = field.options || [];

  const nextOptionUuids = new Set(
    nextOptions.filter((o) => o.uuid).map((o) => o.uuid),
  );

  for (const o of prevOptions) {
    if (o.uuid && !nextOptionUuids.has(o.uuid)) {
      await deleteSelectOption({ uuid: o.uuid });
    }
  }

  const normalizedOptions = nextOptions.map((o, index) => ({
    ...o,
    idx: index + 1,
  }));

  for (let i = 0; i < normalizedOptions.length; i++) {
    const option = normalizedOptions[i];

    const payload = {
      value: option.value,
      idx: i + 1,
      request_form_field_uuid: fieldUuid,
    };

    if (!option.uuid) {
      await createSelectOption(payload);
    } else {
      await updateSelectOption({ uuid: option.uuid, ...payload });
    }
  }
}
